import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X, ExternalLink, Volume2, VolumeX } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher.jsx';
import useStore from '../stores/useStore.jsx';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function NavBar({ closeDetailClick, showDetailBar = false, detailTitle }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { isMuted, setIsMuted, currentScene } = useStore((state) => ({
    isMuted: state.isMuted,
    setIsMuted: state.setIsMuted,
    currentScene: state.currentScene
  }));

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const navLinks = [
    { href: 'https://wiki.grandgarage.eu', label: t('navigation.wiki') },
    { href: 'https://grandgarage.eu', label: t('navigation.makerspace') },
  ];

  return (
    <nav className={cn(
      "fixed top-0 left-0 z-[100] w-full border-b border-white/10 shadow-sm backdrop-blur-md pt-[env(safe-area-inset-top)]",
      currentScene === 4 ? "bg-white/70" : "bg-black/70"
    )}>
      <div className="container mx-auto flex items-center justify-between p-2 md:p-4">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img src="/icons/logo/logo.svg" alt="Logo" className={cn("h-6 w-6 md:h-8 md:w-8", currentScene === 4 ? "fill-black" : "invert")} />
          <span className={cn("font-logo text-lg font-black tracking-tighter md:text-2xl", currentScene === 4 ? "text-black" : "text-white")}>
            {t('title')}
          </span>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:block">
          <NavigationMenu>
            <NavigationMenuList className="gap-2">
              {navLinks.map((link) => (
                <NavigationMenuItem key={link.href}>
                  <NavigationMenuLink
                    href={link.href}
                    target="_blank"
                    className={cn(
                      navigationMenuTriggerStyle(),
                      "bg-transparent font-bold uppercase tracking-wide hover:bg-black/5",
                      currentScene === 4 ? "text-black" : "text-white hover:bg-white/10"
                    )}
                  >
                    {link.label}
                    <ExternalLink className="ml-1 size-3 opacity-50" />
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
              <NavigationMenuItem>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsMuted(!isMuted)}
                    className={cn("hover:bg-black/5", currentScene === 4 ? "text-black" : "text-white hover:bg-white/10")}
                    title={isMuted ? t('sound.unmute') : t('sound.mute')}
                  >
                    {isMuted ? <VolumeX className="size-5" /> : <Volume2 className="size-5" />}
                  </Button>
                  <LanguageSwitcher className={currentScene === 4 ? "text-black" : "text-white"} />
                </div>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Mobile Menu Trigger */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            className={currentScene === 4 ? "text-black" : "text-white"}
          >
            {isOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>

        {/* Mobile Menu Overlay */}
        {isOpen && (
          <div className={cn(
            "absolute top-full left-0 w-full animate-in fade-in slide-in-from-top-2 p-4 shadow-xl backdrop-blur-lg md:hidden",
            currentScene === 4 ? "bg-white/95" : "bg-black/95"
          )}>
            <ul className={cn("flex flex-col space-y-4 font-bold uppercase tracking-wide", currentScene === 4 ? "text-black" : "text-white")}>
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    className={cn("flex items-center justify-between", currentScene === 4 ? "hover:text-black/70" : "hover:text-white/70")}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                    <ExternalLink className="size-4 opacity-50" />
                  </a>
                </li>
              ))}
              <li className={cn("border-t pt-4", currentScene === 4 ? "border-black/10" : "border-white/10")}>
                <div className="flex items-center justify-between px-2">
                  <span className="text-sm font-bold uppercase tracking-wide">
                    {isMuted ? t('sound.unmute') : t('sound.mute')}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsMuted(!isMuted)}
                    className={currentScene === 4 ? "text-black" : "text-white"}
                  >
                    {isMuted ? <VolumeX className="size-6" /> : <Volume2 className="size-6" />}
                  </Button>
                </div>
              </li>
              <li className={cn("border-t pt-4", currentScene === 4 ? "border-black/10" : "border-white/10")}>
                <LanguageSwitcher className="px-2" />
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Detail Bar */}
      {showDetailBar && (
        <div className="flex h-10 items-center border-t border-white/20 bg-black/90 px-4 text-white shadow-inner md:h-12">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={closeDetailClick}
            className="mr-2 text-white hover:bg-white/20 hover:text-white"
          >
            <X className="size-4 md:size-5" />
          </Button>
          <div className="flex-grow text-center">
            <h1 className="text-xs font-black uppercase tracking-widest md:text-sm">
              {detailTitle}
            </h1>
          </div>
          {/* Spacer to keep title centered */}
          <div className="w-8 md:w-10" />
        </div>
      )}
    </nav>
  );
}
